import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'
import type { 
  BudgetAssistantResponses, 
  BudgetAssistantSession, 
  BudgetSuggestion, 
  BudgetAssistantResults 
} from '@flows.cash/types'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

interface BudgetAssistantState {
  // State
  currentSession: BudgetAssistantSession | null
  currentStep: number
  isLoading: boolean
  error: string | null
  lastSaved: number | null
  
  // Data
  responses: BudgetAssistantResponses
  results: BudgetAssistantResults | null
  
  // Actions
  initializeSession: (userId: string) => Promise<void>
  updateResponses: (stepData: Partial<BudgetAssistantResponses>, stepId?: string) => Promise<void>
  setCurrentStep: (step: number) => void
  generateResults: () => BudgetAssistantResults
  generateBudgets: (userId: string) => Promise<void>
  completeSession: (userId: string) => Promise<void>
  clearSession: () => void
}

// Cache duration in milliseconds (30 minutes)
const CACHE_DURATION = 30 * 60 * 1000;

export const useBudgetAssistantStore = create<BudgetAssistantState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSession: null,
      currentStep: 0,
      isLoading: false,
      error: null,
      lastSaved: null,
      responses: {},
      results: null,

      initializeSession: async (userId: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Check for existing in-progress session
          const { data: existingSessions, error: fetchError } = await supabase
            .from('budget_assistant_responses')
            .select('*')
            .eq('user_id', userId)
            .eq('completion_status', 'in_progress')
            .order('created_at', { ascending: false })
            .limit(1);

          if (fetchError) throw fetchError;

          let session: BudgetAssistantSession;

          if (existingSessions && existingSessions.length > 0) {
            // Resume existing session
            const existingSession = existingSessions[0];
            session = {
              id: existingSession.id,
              userId: existingSession.user_id,
              sessionId: existingSession.session_id,
              responses: {
                monthlyIncome: existingSession.monthly_income,
                currency: existingSession.currency,
                rentHousing: existingSession.rent_housing,
                utilities: existingSession.utilities,
                groceries: existingSession.groceries,
                healthcare: existingSession.healthcare,
                loans: existingSession.loans,
                transportation: existingSession.transportation,
                diningOut: existingSession.dining_out,
                entertainment: existingSession.entertainment,
                shopping: existingSession.shopping,
                desiredSavings: existingSession.desired_savings,
                upcomingExpenses: existingSession.upcoming_expenses,
                yearlyExpenses: existingSession.yearly_expenses || {},
                convertYearlyToMonthly: existingSession.convert_yearly_to_monthly ?? true,
                emergencyFundPercentage: existingSession.emergency_fund_percentage,
                miscExpenses: existingSession.misc_expenses,
              },
              completedSteps: existingSession.completed_steps || [],
              completionStatus: existingSession.completion_status,
              budgetsGenerated: existingSession.budgets_generated,
              createdAt: existingSession.created_at,
              updatedAt: existingSession.updated_at,
            };

            set({
              currentSession: session,
              responses: session.responses,
              isLoading: false,
              lastSaved: Date.now()
            });
          } else {
            // Create new session
            const sessionId = crypto.randomUUID();
            const newSessionData = {
              user_id: userId,
              session_id: sessionId,
              currency: 'USD',
              completion_status: 'in_progress',
              completed_steps: [],
              budgets_generated: false,
            };

            const { data: newSession, error: createError } = await supabase
              .from('budget_assistant_responses')
              .insert([newSessionData])
              .select()
              .single();

            if (createError) throw createError;

            session = {
              id: newSession.id,
              userId: newSession.user_id,
              sessionId: newSession.session_id,
              responses: { currency: 'USD' },
              completedSteps: [],
              completionStatus: 'in_progress',
              budgetsGenerated: false,
              createdAt: newSession.created_at,
              updatedAt: newSession.updated_at,
            };

            set({
              currentSession: session,
              responses: { currency: 'USD' },
              currentStep: 0,
              isLoading: false,
              lastSaved: Date.now()
            });
          }
        } catch (error) {
          console.error('Error initializing budget assistant session:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize session',
            isLoading: false,
          });
        }
      },

      updateResponses: async (stepData: Partial<BudgetAssistantResponses>, stepId?: string) => {
        const state = get();
        if (!state.currentSession) {
          set({ error: 'No active session' });
          return;
        }

        try {
          const updatedResponses = { ...state.responses, ...stepData };
          const completedSteps = stepId && !state.currentSession.completedSteps.includes(stepId)
            ? [...state.currentSession.completedSteps, stepId]
            : state.currentSession.completedSteps;

          // Update database
          const updateData = {
            monthly_income: updatedResponses.monthlyIncome,
            currency: updatedResponses.currency,
            rent_housing: updatedResponses.rentHousing,
            utilities: updatedResponses.utilities,
            groceries: updatedResponses.groceries,
            healthcare: updatedResponses.healthcare,
            loans: updatedResponses.loans,
            transportation: updatedResponses.transportation,
            dining_out: updatedResponses.diningOut,
            entertainment: updatedResponses.entertainment,
            shopping: updatedResponses.shopping,
            desired_savings: updatedResponses.desiredSavings,
            upcoming_expenses: updatedResponses.upcomingExpenses,
            yearly_expenses: updatedResponses.yearlyExpenses || {},
            convert_yearly_to_monthly: updatedResponses.convertYearlyToMonthly ?? true,
            emergency_fund_percentage: updatedResponses.emergencyFundPercentage,
            misc_expenses: updatedResponses.miscExpenses,
            completed_steps: completedSteps,
            updated_at: new Date().toISOString(),
          };


          
          const { error } = await supabase
            .from('budget_assistant_responses')
            .update(updateData)
            .eq('id', state.currentSession.id);

          if (error) {
            console.error('Database update error:', error);
            // In development mode without database, warn but continue with local state
            if (error.message?.includes('relation "budget_assistant_responses" does not exist')) {
              console.warn('Database table not found (development mode) - proceeding with local state only');
            } else {
              throw error;
            }
          }

          // Update local state
          set({
            responses: updatedResponses,
            currentSession: {
              ...state.currentSession,
              responses: updatedResponses,
              completedSteps,
              updatedAt: updateData.updated_at,
            },
            lastSaved: Date.now(),
          });
          

        } catch (error) {
          console.error('Error updating responses:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to update responses',
          });
        }
      },

      setCurrentStep: (step: number) => {
        set({ currentStep: step });
      },

      generateResults: (): BudgetAssistantResults => {
        const state = get();
        const responses = state.responses;
        
        if (!responses.monthlyIncome) {
          throw new Error('Monthly income is required to generate results');
        }

        const currency = responses.currency || 'USD';
        const totalIncome = responses.monthlyIncome;

        // Calculate category totals
        const essential = (responses.rentHousing || 0) + (responses.utilities || 0) + 
                         (responses.groceries || 0) + (responses.healthcare || 0) + (responses.loans || 0);
        const important = responses.transportation || 0;
        const flexible = (responses.diningOut || 0) + (responses.entertainment || 0) + 
                        (responses.shopping || 0) + (responses.miscExpenses || 0);
        
        // Fix: Separate emergency fund calculation from regular savings
        const regularSavings = responses.desiredSavings || 0;
        const emergencyFund = responses.emergencyFundPercentage ? (totalIncome * responses.emergencyFundPercentage) / 100 : 0;
        const totalSavings = regularSavings + emergencyFund;

        const totalExpenses = essential + important + flexible;
        const remainingIncome = totalIncome - totalExpenses - totalSavings;

        // Generate suggestions with improved category names for better mapping
        const suggestions: BudgetSuggestion[] = [];
        const expenseMapping = {
          rentHousing: { name: 'Housing', priority: 'essential' as const },
          utilities: { name: 'Utilities', priority: 'essential' as const },
          groceries: { name: 'Groceries', priority: 'essential' as const },
          healthcare: { name: 'Healthcare', priority: 'essential' as const },
          loans: { name: 'Debt & Loans', priority: 'essential' as const },
          transportation: { name: 'Transportation', priority: 'important' as const },
          diningOut: { name: 'Restaurants', priority: 'flexible' as const },
          entertainment: { name: 'Entertainment', priority: 'flexible' as const },
          shopping: { name: 'Shopping', priority: 'flexible' as const },
          miscExpenses: { name: 'Miscellaneous', priority: 'flexible' as const },
        };

        Object.entries(expenseMapping).forEach(([key, { name, priority }]) => {
          const amount = responses[key as keyof BudgetAssistantResponses] as number;
          if (amount && amount > 0) {
            suggestions.push({
              categoryName: name,
              amount,
              period: 'monthly',
              priority,
              description: `Monthly ${name.toLowerCase()} budget`,
              source: key,
            });
          }
        });

        // Add regular savings as a suggestion
        if (regularSavings > 0) {
          suggestions.push({
            categoryName: 'Savings',
            amount: regularSavings,
            period: 'monthly',
            priority: 'essential',
            description: 'Monthly savings goal',
            source: 'desiredSavings',
          });
        }

        // Add emergency fund as a separate suggestion if exists
        if (emergencyFund > 0) {
          suggestions.push({
            categoryName: 'Emergency Fund',
            amount: emergencyFund,
            period: 'monthly',
            priority: 'essential',
            description: 'Emergency fund contribution',
            source: 'emergencyFundPercentage',
          });
        }

        // Add yearly expenses as either monthly savings goals or yearly budgets
        if (responses.yearlyExpenses && typeof responses.yearlyExpenses === 'object') {
          const yearlyExpenseMapping = {
            vacation: { name: 'Vacation & Travel Fund', priority: 'important' as const },
            tech: { name: 'Tech & Equipment Fund', priority: 'flexible' as const },
            vehicle: { name: 'Vehicle Maintenance Fund', priority: 'important' as const },
            insurance: { name: 'Insurance & Fees Fund', priority: 'essential' as const },
            holidays: { name: 'Holiday & Gifts Fund', priority: 'flexible' as const },
            education: { name: 'Education & Courses Fund', priority: 'important' as const },
            other: { name: 'Other Yearly Expenses Fund', priority: 'flexible' as const },
          };

          const convertToMonthly = responses.convertYearlyToMonthly ?? true;

          Object.entries(responses.yearlyExpenses).forEach(([key, yearlyAmount]) => {
            if (yearlyAmount && yearlyAmount > 0) {
              const mapping = yearlyExpenseMapping[key as keyof typeof yearlyExpenseMapping];
              
              if (mapping) {
                if (convertToMonthly) {
                  // Create monthly savings goal
                  const monthlyAmount = yearlyAmount / 12;
                  suggestions.push({
                    categoryName: mapping.name,
                    amount: monthlyAmount,
                    period: 'monthly',
                    priority: mapping.priority,
                    description: `Monthly savings for yearly ${key} expenses`,
                    source: `yearlyExpenses.${key}`,
                  });
                } else {
                  // Create yearly budget
                  suggestions.push({
                    categoryName: mapping.name,
                    amount: yearlyAmount,
                    period: 'yearly',
                    priority: mapping.priority,
                    description: `Annual budget for ${key} expenses`,
                    source: `yearlyExpenses.${key}`,
                  });
                }
              }
            }
          });
        }

        // Enhanced tips and warnings generation
        const tips: string[] = [];
        const warnings: string[] = [];

        const essentialPercentage = (essential / totalIncome) * 100;
        const savingsPercentage = (totalSavings / totalIncome) * 100;
        const flexiblePercentage = (flexible / totalIncome) * 100;
        const housingPercentage = ((responses.rentHousing || 0) / totalIncome) * 100;

        // Budget Health Tips
        if (savingsPercentage >= 20) {
          tips.push('Excellent savings rate! You\'re building a strong financial foundation.');
        } else if (savingsPercentage >= 15) {
          tips.push('Great job saving! You\'re on track for financial security.');
        } else if (savingsPercentage >= 10) {
          tips.push('Good savings start! Consider gradually increasing to 15-20% for optimal financial health.');
        }

        // Housing Tips
        if (housingPercentage <= 30) {
          tips.push('Your housing costs are well within the recommended 30% of income. Great job!');
        } else if (housingPercentage <= 40) {
          tips.push('Housing costs are slightly elevated. Consider this when evaluating other expenses.');
        }

        // 50/30/20 Rule Insights
        const needsPercentage = essentialPercentage + (important / totalIncome) * 100;
        if (needsPercentage <= 50 && flexiblePercentage <= 30 && savingsPercentage >= 20) {
          tips.push('Perfect! You\'re following the 50/30/20 rule - 50% needs, 30% wants, 20% savings.');
        } else if (needsPercentage <= 55) {
          tips.push('You\'re close to the ideal 50/30/20 budget ratio. Small adjustments can optimize your plan.');
        }

        // Emergency Fund Tips
        if (responses.emergencyFundPercentage && responses.emergencyFundPercentage >= 5) {
          tips.push('Smart move setting aside money for emergencies! This will provide peace of mind.');
        } else if (!responses.emergencyFundPercentage || responses.emergencyFundPercentage === 0) {
          tips.push('Consider allocating 5-10% for an emergency fund to cover unexpected expenses.');
        }

        // Debt Management Tips
        if ((responses.loans || 0) > 0) {
          const debtPercentage = ((responses.loans || 0) / totalIncome) * 100;
          if (debtPercentage <= 20) {
            tips.push('Your debt payments are manageable. Focus on paying them off to free up more money for savings.');
          }
        }

        // Flexible Spending Tips
        if (flexiblePercentage <= 25) {
          tips.push('You\'re doing great with discretionary spending! This leaves room for savings and unexpected expenses.');
        } else if (flexiblePercentage <= 35) {
          tips.push('Moderate flexible spending. Consider tracking these categories to find potential savings opportunities.');
        }

        // Warnings for problematic ratios
        if (essentialPercentage > 60) {
          warnings.push('Essential expenses are high (>60% of income). Consider reducing housing or debt payments.');
        }

        if (housingPercentage > 40) {
          warnings.push('Housing costs exceed 40% of income. This may limit your ability to save and spend on other priorities.');
        }

        if (savingsPercentage < 5) {
          warnings.push('Very low savings rate. Try to save at least 10-20% of your income for a healthy financial future.');
        } else if (savingsPercentage < 10) {
          warnings.push('Try to increase savings to at least 10-15% of your income for better financial security.');
        }

        if (remainingIncome < 0) {
          warnings.push(`Expenses exceed income by $${Math.abs(remainingIncome).toFixed(0)}. Review your spending plans.`);
        } else if (remainingIncome < totalIncome * 0.05) {
          warnings.push('Very tight budget with little margin for error. Consider building in some flexibility.');
        }

        if (flexiblePercentage > 40) {
          warnings.push('High discretionary spending (>40% of income). Consider reducing flexible expenses to increase savings.');
        }

        // Add motivational tip if everything looks good
        if (tips.length > 0 && warnings.length === 0 && remainingIncome > 0) {
          tips.push('Your budget looks well-balanced! Stick to this plan and review monthly to stay on track.');
        }

        const results: BudgetAssistantResults = {
          totalIncome,
          totalExpenses,
          totalSavings,
          remainingIncome,
          suggestions,
          tips,
          warnings,
          currency,
          breakdown: { essential, important, flexible, savings: totalSavings },
        };

        set({ results });
        return results;
      },

      generateBudgets: async (userId: string) => {
        const state = get();
        if (!state.results) {
          throw new Error('No results available for budget generation');
        }

        set({ isLoading: true, error: null });

        try {
          const { useBudgetStore } = await import('./useBudgetStore');
          const { useCategoriesStore } = await import('./useCategoriesStore');
          const budgetStore = useBudgetStore.getState();
          const categoriesStore = useCategoriesStore.getState();

          // Ensure categories are loaded
          await categoriesStore.fetchCategories(userId);
          const categories = categoriesStore.categories;
          
          // If no categories are loaded, we have a problem
          if (categories.length === 0) {
            throw new Error('Categories must be loaded before generating budgets');
          }

          // Helper function to find category by name (case-insensitive)
          const findCategoryByName = (name: string): string | null => {
            const normalizedName = name.toLowerCase();
            
            // First, check main categories
            for (const category of categories) {
              if (category.name.toLowerCase() === normalizedName) {

                return category.id;
              }
            }
            
            // Then check subcategories - prioritize exact matches
            for (const category of categories) {
              if (category.subcategories) {
                for (const subcategory of category.subcategories) {
                  if (subcategory.name.toLowerCase() === normalizedName) {
                    return subcategory.id;
                  }
                }
              }
            }
            
            // Enhanced category mappings with fallbacks based on actual default category structure
            const categoryMappings: Record<string, { primary: string[], fallback: string, preferSubcategory?: boolean }> = {
              'housing': { primary: ['housing', 'rent/mortgage'], fallback: 'housing' },
              'utilities': { primary: ['utilities'], fallback: 'utilities' },
              'groceries': { primary: ['groceries'], fallback: 'food', preferSubcategory: true },
              'restaurants': { primary: ['restaurants', 'dining out'], fallback: 'food', preferSubcategory: true },
              'healthcare': { primary: ['healthcare'], fallback: 'health' },
              'debt & loans': { primary: ['debt & loans', 'loans'], fallback: 'financial' },
              'transportation': { primary: ['transportation'], fallback: 'transportation' },
              'entertainment': { primary: ['entertainment'], fallback: 'entertainment' },
              'shopping': { primary: ['shopping', 'clothing'], fallback: 'shopping' },
              'miscellaneous': { primary: ['miscellaneous', 'other'], fallback: 'miscellaneous' },
              'savings': { primary: ['savings & investments', 'savings'], fallback: 'savings & investments' },
              'emergency fund': { primary: ['emergency fund', 'savings'], fallback: 'savings & investments', preferSubcategory: true },
              // Yearly expense fund mappings to correct default categories
              'vacation & travel fund': { primary: ['vacation', 'travel'], fallback: 'travel' },
              'tech & equipment fund': { primary: ['technology', 'electronics'], fallback: 'shopping', preferSubcategory: true },
              'vehicle maintenance fund': { primary: ['transport', 'vehicle', 'car'], fallback: 'transportation', preferSubcategory: true },
              'insurance & fees fund': { primary: ['insurance'], fallback: 'financial', preferSubcategory: true },
              'holiday & gifts fund': { primary: ['gifts'], fallback: 'gifts & donations', preferSubcategory: true },
              'education & courses fund': { primary: ['education'], fallback: 'education', preferSubcategory: true },
              'other yearly expenses fund': { primary: ['other', 'yearly'], fallback: 'miscellaneous', preferSubcategory: true }
            };
            
            // Try to find exact match in mappings
            for (const [mappingKey, mapping] of Object.entries(categoryMappings)) {
              // Check if the fund name matches the mapping key exactly
              if (mappingKey.toLowerCase() === normalizedName) {

                
                // For utilities, always prefer main category
                if (mappingKey === 'utilities') {
                  for (const category of categories) {
                    if (category.name.toLowerCase() === 'utilities') {
                      return category.id;
                    }
                  }
                }
                
                // For subcategories (like groceries, restaurants), look for subcategory first
                if (mapping.preferSubcategory) {
                  for (const category of categories) {
                    if (category.subcategories) {
                      for (const subcategory of category.subcategories) {
                        if (subcategory.name.toLowerCase() === mappingKey) {
                          return subcategory.id;
                        }
                      }
                    }
                  }
                }
                
                // Look for exact main category name
                for (const category of categories) {
                  if (category.name.toLowerCase() === mappingKey) {
                    return category.id;
                  }
                }
                
                // Look for exact subcategory name  
                for (const category of categories) {
                  if (category.subcategories) {
                    for (const subcategory of category.subcategories) {
                      if (subcategory.name.toLowerCase() === mappingKey) {
                        return subcategory.id;
                      }
                    }
                  }
                }
                
                // Fallback to the fallback category - try exact match first, then partial match
                for (const category of categories) {
                  if (category.name.toLowerCase() === mapping.fallback.toLowerCase()) {
                    return category.id;
                  }
                }
                
                // If exact match fails, try partial match
                for (const category of categories) {
                  if (category.name.toLowerCase().includes(mapping.fallback.toLowerCase()) || 
                      mapping.fallback.toLowerCase().includes(category.name.toLowerCase())) {
                    return category.id;
                  }
                }
              }
            }
            
            // If nothing found, try to find the first expense category as last resort
            const firstExpenseCategory = categories.find(c => c.type === 'expense');
            if (firstExpenseCategory) {
              return firstExpenseCategory.id;
            }
            
            return null;
          };

          // Create budgets for each suggestion
          await Promise.all(
            state.results.suggestions.map(async (suggestion) => {
              // Try to find a matching category
              const categoryId = findCategoryByName(suggestion.categoryName);
              
              // Calculate end date based on budget period
              const currentDate = new Date();
              const endDate = suggestion.period === 'yearly' 
                ? new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), currentDate.getDate())
                : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
              
              const budget = {
                userId: userId,
                name: suggestion.categoryName,
                amount: suggestion.amount,
                currency: state.results!.currency,
                originalAmount: suggestion.amount,
                originalCurrency: state.results!.currency,
                categoryId: categoryId || '',
                period: suggestion.period as 'monthly' | 'yearly',
                startDate: new Date().toISOString(),
                endDate: endDate.toISOString(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                // Additional fields for database compatibility
                start_date: new Date().toISOString(),
                end_date: endDate.toISOString(),
                category_id: categoryId || '',
                source: 'generated_by_assistant',
                assistant_session_id: state.currentSession?.sessionId,
              };
              
              return budgetStore.addBudget(userId, budget);
            })
          );

          // Mark budgets as generated
          if (state.currentSession) {
            await supabase
              .from('budget_assistant_responses')
              .update({ budgets_generated: true })
              .eq('id', state.currentSession.id);

            set({
              currentSession: { ...state.currentSession, budgetsGenerated: true },
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Error generating budgets:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to generate budgets',
            isLoading: false,
          });
          throw error;
        }
      },

      completeSession: async (userId: string) => {
        const state = get();
        if (!state.currentSession) return;

        try {
          await supabase
            .from('budget_assistant_responses')
            .update({ completion_status: 'completed' })
            .eq('id', state.currentSession.id);

          set({
            currentSession: { ...state.currentSession, completionStatus: 'completed' },
          });
        } catch (error) {
          console.error('Error completing session:', error);
        }
      },

      clearSession: () => {
        set({
          currentSession: null,
          currentStep: 0,
          responses: {},
          results: null,
          error: null,
          lastSaved: null,
        });
      },
    }),
    {
      name: 'budget-assistant-storage',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({
        currentStep: state.currentStep,
        responses: state.responses,
        lastSaved: state.lastSaved,
      }),
    }
  )
) 